// §4 S5's DEFECT HAND-OFF LEDGER and its disposition actions (Module 04, slice
// 04-C-5 — spec 04 §2.11, §3.7, §5 e18/e19).
//
// §4's S5 pairs the scoreboard with this panel; until 04-C-5 it was a
// stated-pending note naming the owner, which is the rule for a surface whose
// table does not exist. The table exists now, so the panel does.
//
// WHAT THE DESIGN COMMITS TO, and what each commitment protects against:
//
//  * EVERY ROW NAMES BOTH SHIFTS, by CODE and DATE, not by id. A hand-off is
//    one shift's accusation about another's work, and a supervisor deciding it
//    is deciding WHOSE number moves. Two uuids would make that decision
//    unreviewable — the same argument that put `shift_definition_code` on the
//    scoreboard row above it.
//  * THE DECISION IS ON THE GLASS, not only on the wire. This repository has
//    shipped an API-only action three times on the S2 screen (§4's head notes
//    record all three), and every one of them was found by a walkthrough going
//    to click a control that was not there. The three §5 e19 dispositions are
//    three buttons in the row, and the walkthrough drives the buttons.
//  * SCRAP DEMANDS A REASON, CLIENT-SIDE, IN THE SUPERVISOR'S OWN LANGUAGE.
//    The server refuses a note-less scrap 422 (it moves a quantity onto another
//    shift's yield), and meeting that refusal as a red toast after the click is
//    worse than being told before it. The server's rule is mirrored EXACTLY —
//    required for scrap, optional for the other two — never more and never
//    less, because a client that demands more than the server is a client that
//    blocks legitimate work.
//  * A DECIDED ROW SHOWS ITS DECISION AND OFFERS NO BUTTONS. The decision is
//    one-shot (§2.11's grain CHECK: a disposed row always names who and when),
//    so an enabled button on a decided row would be a 409 waiting to happen.
//  * REWORK ON AN ORDER THAT IS NO LONGER LIVE IS DISABLED, NOT HIDDEN, AND
//    SAYS WHAT REMAINS (D-299 ruling 8, spec 04 §4 S5). e19 refuses a rework
//    409 `REWORK_BLOCKED_WO_NOT_LIVE` once the work order has left
//    `released`/`in_progress`, and §3.1's close guard deliberately leaves a
//    completed order carrying a pending hand-off, so that row is the normal
//    case. A live button there is a dead control (the #229 class); a MISSING
//    one would leave a supervisor wondering where the third decision went. The
//    sentence names what still applies and, for a COMPLETED order only, where
//    the re-do goes — a cancelled order receipted nothing, so "the lots already
//    in stock" would name goods that do not exist (D-299 owner ruling F3).
//  * A CLOSED ORDER OFFERS NO DECISION AT ALL, and says so in one sentence
//    (D-299 owner rulings, 2026-09-25; spec 04 §4 S5). e19 refuses all three
//    there as §9.4's `WO_CLOSED_IMMUTABLE`, so Accept as is and Scrap would be
//    two more dead controls. The state is unreachable in the ordinary case —
//    §3.1's close refuses over a pending hand-off — and "cannot normally
//    happen" is not a guarantee, so the row is still worded, never blank.
//  * A REFUSED DECISION SAYS WHICH DECISIONS STILL APPLY, from the server's
//    own list (D-299 owner rulings F1/F2). e19's two content refusals —
//    `REWORK_BLOCKED_WO_NOT_LIVE` and the C6 fence's
//    `QUALITY_SCRAP_ALREADY_ATTRIBUTED` — carry `available_dispositions`,
//    DERIVED from the real guards, so the sentence under the error card names
//    exactly the buttons that would be accepted and nothing the next click
//    would refuse. The ledger is also re-read on any refusal: a rework refused
//    because the order just completed must leave the row showing it disabled.
//  * NULL `produced_by_worker_id` IS WORDED, never blank. §2.11 leaves it null
//    when the covering qty window had a CREW, and "—" in that cell reads as
//    missing data rather than as the honest answer it is.
//  * THE PANEL DOES NOT EXIST FOR A ROLE THAT CANNOT READ IT. e18 is gated on
//    `production.approve_shift_handoff` while S5 itself is gated on the much
//    wider `production.scoreboard.read` — so planner, finance, maintenance and
//    floor_worker are entitled to the board and not to the ledger. Gating only
//    the BUTTONS left all four of them with three 403s per page load, a red
//    error panel and a Retry that could never succeed (adversarial review,
//    2026-08-07). The S1–S4 rule is that a surface a role cannot read is not
//    rendered at all, and the read is gated on the same code as the decision
//    because e18 IS the disposition queue.
//
// NO MONEY: a hand-off carries quantities and reason codes only (§2.11).

import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { joinSentences } from "../../../../lib/sentences";
import {
  useDefectHandoffs,
  useDisposeDefectHandoff,
  useScrapReasons,
  type DefectHandoff,
  type DefectHandoffQuery,
} from "../hooks/production-queries";
import { formatQty, localeLabel } from "../lib/format";
import { enumLabel } from "../../../../lib/enum-label";

/** The three §5 e19 values. `pending` is absent by construction: this endpoint
 * DECIDES, and "decide that it is undecided" is not a decision. */
const DISPOSITIONS = ["rework", "scrap", "accepted_as_is"] as const;
type Disposition = (typeof DISPOSITIONS)[number];

/** Whether e19 would re-open the producing step for `rework` (D-299 ruling 1):
 * only a `released` or `in_progress` order's steps re-open. The row carries the
 * hand-off's OWN work order's status; the server guards on the producing
 * operation's order, which the flag applier makes the same order (D-299
 * correction 4) — so this mirrors the refusal rather than guessing at it. */
function reworkReopens(row: DefectHandoff): boolean {
  return row.work_order_status === "released" || row.work_order_status === "in_progress";
}

/** Whether e19 would decide this hand-off AT ALL. A `closed` order refuses
 * every disposition 409 `WO_CLOSED_IMMUTABLE` (§9.4) before any other guard
 * runs, so the ledger offers none — not Rework, and not the two that stay live
 * on a completed or cancelled order (D-299 owner rulings; spec 04 §4 S5). */
function orderClosed(row: DefectHandoff): boolean {
  return row.work_order_status === "closed";
}

/** The label key of each §5 e19 decision, shared by the buttons and the
 * refusal sentence so one decision reads the same in both places. */
const DECISION_LABEL: Record<Disposition, string> = {
  rework: "production.shifts.handoffRework",
  scrap: "production.shifts.handoffScrap",
  accepted_as_is: "production.shifts.handoffAccept",
};

/**
 * The sentence under a refused decision (D-299 owner rulings F1/F2), or
 * undefined when the refusal carries nothing this panel can word better than
 * its title. Built from the problem's TOP-LEVEL extensions — never from the
 * server's English `detail` — so it is in the reader's language and names
 * only the decisions `available_dispositions` says the server would accept.
 */
export function decideRefusalDetail(
  t: TFunction,
  language: string,
  error: unknown,
): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  const ext = (error.problem ?? {}) as Record<string, unknown>;
  const offered = Array.isArray(ext["available_dispositions"])
    ? (ext["available_dispositions"] as unknown[]).filter((d): d is Disposition =>
        (DISPOSITIONS as readonly unknown[]).includes(d),
      )
    : [];
  // `accepted_as_is` always applies (ruling 9), so an empty or unreadable
  // list still names the one decision that un-sticks the order.
  const decisions = new Intl.ListFormat(language, { type: "disjunction" }).format(
    (offered.length > 0 ? offered : (["accepted_as_is"] as const)).map((d) => t(DECISION_LABEL[d])),
  );
  if (error.code === "REWORK_BLOCKED_WO_NOT_LIVE") {
    const status = ext["work_order_status"];
    return t("production.shifts.handoffRefusedNotLive", {
      status:
        typeof status === "string"
          ? enumLabel(t, "production.status.wo", status)
          : t("production.common.notAvailable"),
      decisions,
    });
  }
  if (error.code === "QUALITY_SCRAP_ALREADY_ATTRIBUTED") {
    return t("production.shifts.handoffRefusedScrapAttributed", { decisions });
  }
  if (error.code === "WO_CLOSED_IMMUTABLE") return t("production.problem.WO_CLOSED_IMMUTABLE");
  return undefined;
}

export interface HandoffLedgerProps {
  /** The scoreboard's own filter, narrowed to this facility and shift date's
   * instances. Undefined disables the read — which happens for TWO reasons the
   * panel must tell apart (see `boardPending`): the board has not resolved its
   * facility/rows yet, or it HAS and the date simply has no shift instances. */
  query: DefectHandoffQuery | undefined;
  /** Whether the SCOREBOARD read above this panel is still in flight. Without
   * it an undefined `query` is ambiguous, and the panel showed a skeleton for
   * BOTH readings — honest for "not yet", an eternal lie for "empty day": a
   * disabled TanStack query stays `isPending` forever, so a date with no shift
   * instances held a permanent skeleton (W-SWEEP, red in all four locale ×
   * viewport combinations, 2026-08-14). */
  boardPending: boolean;
  /** Whether the session holds `production.approve_shift_handoff` — which e18
   * requires to READ and e19 to DECIDE, one code for both. False renders
   * nothing at all rather than a panel whose every fetch is a 403. */
  canDispose: boolean;
}

export function HandoffLedger({ query, boardPending, canDispose }: HandoffLedgerProps) {
  const { t, i18n } = useTranslation();
  // The same catalog S2's reports panel and the record form read (one fetch —
  // react-query dedupes it), so one scrap cause reads the same everywhere.
  const scrapReasons = useScrapReasons();
  const reasonLabel = (code: string): string | undefined => {
    const cause = (scrapReasons.data ?? []).find((c) => c.code === code);
    return cause === undefined ? undefined : localeLabel(cause.name_i18n, i18n.language);
  };
  // GATED ON THE PERMISSION, not just on the query. e18 answers 403 without
  // `production.approve_shift_handoff`, so firing it for a planner produced a
  // permanent unretryable error panel on a screen they are entitled to read.
  const ledger = useDefectHandoffs(query ?? {}, canDispose && query !== undefined);
  const dispose = useDisposeDefectHandoff();
  /** Which row's note box is open, and what is in it. One at a time: a table of
   * always-open note fields is a table nobody reads. */
  const [draft, setDraft] = useState<{ id: string; disposition: Disposition; note: string } | null>(
    null,
  );

  const items = ledger.data?.items ?? [];

  // A role without the code sees NO PANEL — not an empty one, and above all not
  // an error one. The heading itself would be a claim that this screen has a
  // hand-off ledger they simply cannot load.
  if (!canDispose) return null;

  // WHAT "LOADING" MEANS HERE, threaded rather than guessed. A disabled
  // TanStack query stays `isPending` forever, so `ledger.isPending` only
  // answers "loading?" while the query is ENABLED (`query !== undefined`).
  // While it is disabled the honest answer comes from the BOARD: still
  // resolving its facility/rows → skeleton; resolved to a date with no shift
  // instances → the empty state, because "no shifts" entails "no hand-offs
  // between them" and a skeleton there never resolves into anything (W-SWEEP,
  // 2026-08-14 — every empty day wore a permanent skeleton).
  const loading = query === undefined ? boardPending : ledger.isPending;

  // The cell-level `data-testid`s the certified M04-S05/S06 walkthroughs address
  // (`row.getByTestId("handoff-produced")`) ride a span inside the cell now that
  // the `<td>` belongs to the primitive — the same element tree, one node
  // deeper, and every assertion on them is a text assertion.
  const columns: DataTableColumn<DefectHandoff>[] = [
    {
      // WHOSE NUMBER MOVES — the producing shift, by code and date, and the
      // worker only when §2.11 identified one.
      key: "produced_shift",
      header: t("production.shifts.handoffColProduced"),
      id: true,
      render: (row) => (
        <span data-testid="handoff-produced">
          {t("production.shifts.handoffShiftCell", {
            code: row.produced_shift_definition_code,
            date: row.produced_shift_date,
          })}
          {row.produced_by_worker_id === null ? (
            <span className="handoff-crew" data-testid="handoff-crew">
              {t("production.shifts.handoffWorkerUnknown")}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "flagged_shift",
      header: t("production.shifts.handoffColFlagged"),
      render: (row) =>
        t("production.shifts.handoffShiftCell", {
          code: row.flagged_shift_definition_code,
          date: row.flagged_shift_date,
        }),
    },
    {
      key: "qty_defective",
      header: t("production.shifts.handoffColQty"),
      numeric: true,
      render: (row) => <span data-testid="handoff-qty">{formatQty(row.qty_defective)}</span>,
    },
    {
      key: "scrap_reason_code",
      header: t("production.shifts.handoffColReason"),
      render: (row) => (
        <>
          {/* THE OPERATOR'S OWN WORDS, not the wire code (builder report,
              2026-08-12). This cell printed `MACHINE_DRIFT` while the three
              sibling surfaces that show the same field — the S2 reports panel,
              the record form and the Events timeline — all resolve it through
              `useScrapReasons` to the pack's localized name. A supervisor
              deciding whose yield moves was reading a constant; the operator who
              flagged it chose "Дрейф станка". Falls back to the code for a cause
              retired since the hand-off was raised. */}
          {reasonLabel(row.scrap_reason_code) ?? row.scrap_reason_code}
          {row.photo_file_ids.length > 0 ? (
            <span className="handoff-photos">
              {t("production.shifts.handoffPhotos", { n: row.photo_file_ids.length })}
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: "disposition",
      header: t("production.shifts.handoffColState"),
      render: (row) => (
        <span data-testid="handoff-state">
          {t(`production.shifts.handoffDisposition.${row.disposition}`)}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("production.common.actions"),
      actions: true,
      render: (row) => {
        const closed = orderClosed(row);
        const reworkBlocked = !reworkReopens(row);
        // A note opened BEFORE a refetch showed the order moving on is closed
        // with its button: a rework note once the order left `in_progress`,
        // and ANY note once it is `closed`. Its Confirm could only post the
        // 409 the missing or disabled button exists to spare the supervisor.
        const open =
          draft?.id === row.id && !closed && !(reworkBlocked && draft.disposition === "rework")
            ? draft
            : null;
        const pending = row.disposition === "pending";
        const blockedHintId = `handoff-rework-blocked-${row.id}`;
        return (
          <>
            {/* A DECIDED row offers nothing: the decision is one-shot, so an
                enabled button here is a 409 waiting to happen. `canDispose` is
                not re-checked — the whole panel returns null without it, since
                e18 needs the same code to READ. A pending row on a CLOSED
                order offers nothing either: e19 refuses all three there. */}
            {pending && !closed ? (
              <div className="handoff-buttons">
                {/* `disabled`, not `busy`: the in-flight mutation is the
                    Confirm's, and a spinner on all three openers would claim
                    work they are not doing. Rework is ALSO disabled on an
                    order that is no longer live (D-299) — rendered, so the
                    row still shows all three decisions, and described by the
                    sentence below it so a screen reader hears why. */}
                {DISPOSITIONS.map((d) => (
                  <Button
                    key={d}
                    data-testid={`handoff-${d}`}
                    disabled={dispose.isPending || (d === "rework" && reworkBlocked)}
                    aria-describedby={d === "rework" && reworkBlocked ? blockedHintId : undefined}
                    onClick={() => {
                      setDraft({ id: row.id, disposition: d, note: "" });
                    }}
                  >
                    {t(DECISION_LABEL[d])}
                  </Button>
                ))}
              </div>
            ) : null}
            {/* THE SENTENCE A DISABLED CONTROL OWES (D-299 ruling 9): a guard
                whose way out is not named on the glass is D-084's failure.
                Pending rows only — a decided row offers nothing to explain. */}
            {pending && !closed && reworkBlocked ? (
              <p id={blockedHintId} data-testid="handoff-rework-blocked">
                {/* The lots sentence only where lots exist: `complete()`
                    receipted a COMPLETED order's output; a cancelled one
                    receipted nothing (D-299 owner ruling F3). */}
                {joinSentences(
                  [
                    t("production.shifts.handoffReworkBlockedNoStock", {
                      status: t(`production.status.wo.${row.work_order_status}`),
                    }),
                    row.work_order_status === "completed"
                      ? t("production.shifts.handoffReworkStock")
                      : "",
                  ],
                  i18n.language,
                )}
              </p>
            ) : null}
            {/* A CLOSED order: no control, one sentence (spec 04 §4 S5). */}
            {pending && closed ? (
              <p data-testid="handoff-order-closed">
                {t("production.shifts.handoffOrderClosed", {
                  status: t(`production.status.wo.${row.work_order_status}`),
                })}
              </p>
            ) : null}

            {open !== null ? (
              <form
                className="handoff-note"
                data-testid="handoff-note-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  dispose.mutate(
                    {
                      id: open.id,
                      disposition: open.disposition,
                      ...(open.note.trim() === "" ? {} : { note: open.note.trim() }),
                    },
                    {
                      onSuccess: () => {
                        setDraft(null);
                      },
                      // A refusal is the server holding a truth this row may
                      // not show yet (the order completed, quality posted the
                      // scrap) — re-read so the row catches up with it.
                      onError: () => {
                        void ledger.refetch();
                      },
                    },
                  );
                }}
              >
                <Field label={t("production.shifts.handoffNoteLabel")}>
                  <input
                    type="text"
                    data-testid="handoff-note"
                    value={open.note}
                    // The SERVER's rule, mirrored exactly: required for scrap
                    // (it moves a quantity onto another shift's yield), optional
                    // otherwise. Demanding more here than the server does would
                    // block legitimate work.
                    required={open.disposition === "scrap"}
                    onChange={(e) => {
                      setDraft({ ...open, note: e.target.value });
                    }}
                  />
                </Field>
                <p className="handoff-note-hint">{t("production.shifts.handoffNoteHint")}</p>
                {/* type="submit" explicitly — Button defaults to type="button"
                    and this control submits the note <form>. */}
                <Button
                  type="submit"
                  data-testid="handoff-confirm"
                  busy={dispose.isPending}
                  busyLabel={t("production.shifts.handoffDeciding")}
                >
                  {t("production.common.confirm")}
                </Button>
                <Button
                  data-testid="handoff-cancel"
                  onClick={() => {
                    setDraft(null);
                  }}
                >
                  {t("production.common.cancel")}
                </Button>
              </form>
            ) : null}
          </>
        );
      },
    },
  ];

  return (
    <section className="shifts-handoffs" data-testid="shifts-handoffs">
      <h2>{t("production.shifts.handoffTitle")}</h2>

      {/* LOADING COVERS THE UNRESOLVED-FACILITY CASE TOO — via `loading`
          above, never via `ledger.isPending` alone. The first cut of this rule
          (2026-08-07) gated the skeleton on the raw flag, which fixed the bare
          heading and installed the opposite defect: on a date with NO shift
          instances the disabled query held `isPending` forever and the
          skeleton never resolved. The skeleton is the honest answer to "not
          yet"; the empty state below is the honest answer to "nothing". */}
      {loading ? <TableSkeleton rows={3} label={t("production.common.loading")} /> : null}

      {ledger.error !== null ? (
        <ErrorState
          title={t("production.shifts.handoffError")}
          retryLabel={t("production.common.retry")}
          requestIdLabel={t("production.common.requestId")}
          requestId={requestIdOf(ledger.error)}
          onRetry={() => {
            void ledger.refetch();
          }}
        />
      ) : null}

      {/* Covers BOTH empty readings: a loaded ledger with zero rows, and a
          board that resolved to a date with no shift instances (query
          disabled, `ledger.error` null by construction — a disabled query
          never ran, so nothing failed). */}
      {!loading && ledger.error === null && items.length === 0 ? (
        <EmptyState
          title={t("production.shifts.handoffTitle")}
          body={t("production.shifts.handoffPending")}
        />
      ) : null}

      {items.length > 0 ? (
        <DataTable
          data-testid="handoff-table"
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("production.shifts.handoffCaption")}
          rowTestId="handoff-row"
          rowProps={(row) => ({ "data-handoff": row.id })}
        />
      ) : null}

      {dispose.error !== null ? (
        <ErrorState
          title={t("production.shifts.handoffDecideError")}
          detail={decideRefusalDetail(t, i18n.language, dispose.error)}
          retryLabel={t("production.common.retry")}
          requestIdLabel={t("production.common.requestId")}
          requestId={requestIdOf(dispose.error)}
          onRetry={() => {
            dispose.reset();
          }}
        />
      ) : null}
    </section>
  );
}
